import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { VsiH4, VsiHr, VsiLgText, VsiP, VsiSpanText } from "../../styled/App.styled";
import {
	Input,
	Label,
	Row,
	Col,
	FormGroup,
	Form,
	Button,
	FormFeedback,
	Spinner,
} from "reactstrap";
import { useTitleFromComponent } from "../../utils/hooks";
import { useVsiAppContext } from "../../stores/VsiApp";
import { roles } from "../../utils/utils";
import { GetAuth, PutAuth, DeleteAuth } from "../../utils/Verbs";
import { useSearchParams } from "react-router-dom";
import VsiModal from "../../components/VsiModal";
import { capitalizeName } from "../../utils/Helpers";

const defaultValids = {
	firstName: true,
	lastName: true,
	email: true,
	company: true,
	uiTimeout: true,
};

export default function EditUser({ context, title }) {
	useTitleFromComponent(`${context} / ${title}`);
	let navigate = useNavigate();
	const { theme, user, getAccessTokenSilently } = useVsiAppContext();
	
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [email, setEmail] = useState('');
	const [userType, setUserType] = useState({});
	const [company, setCompany] = useState({});
	const [uiTimeout, setUiTimeout] = useState(30);
	const [clients, setClients] = useState([]);
	
	const [validInputs, setValidInputs] = useState(defaultValids);
	const [validCheck, setValidCheck] = useState(false);
	const [isPosting, setIsPosting] = useState(false);
	const [isSuccessful, setIsSuccessful] = useState(false);
	
	const [error, setError] = useState(null);
	const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
	const [availableRoles, setAvailableRoles] = useState([]);
	
	const [searchParams] = useSearchParams();
	
	useEffect(() => {
		if(user.role === 'super_user') {
			loadClientsList();
		} else {
			setClients([user.client])
		}

		if(user.role === 'local_admin') {
			setAvailableRoles([
				{
					value: "basic_user",
					label: 'Basic User',
				},
				{
					value: "local_admin",
					label: 'Local Administrator',
				}
			])
		} else {
			setAvailableRoles(roles)
		}
	}, []);

	useEffect(() => {
		getUser();
	}, [clients]);
	
	const loadClientsList = async () => {
		try {
			const token = await getAccessTokenSilently();
			const resp = await GetAuth('/api/v6/admin/clients', token);
			const data = resp?.data || [];
			setClients(data);
		} catch (e) {
			console.error(e);
		}
	};
	
	const getUser = async function () {
		try {
			const token = await getAccessTokenSilently();
			const editUser = await GetAuth(`/api/v6/admin/users/${searchParams.get('user_id')}`, token);
			if (editUser) {
				setDefaultState(editUser?.data || {});
			};
		} catch (err) {
			setError(err);
		}
	};
	
	const setDefaultState = (user) => {
		setFirstName(user.firstName);
		setLastName(user.lastName);
		setEmail(user.email);
		const clientData = clients.find(({ clientId }) => clientId === user?.client?.clientId ?? user?.clientId);
		const foundRole = roles.find(role => role.value === user?.role);
		setUserType(	foundRole);
		setCompany({
			clientId: user?.client?.clientId,
			name: user?.client?.companyName || clientData?.name,
			label: user?.client?.companyName || clientData?.name,
		});
		setUiTimeout(user.uiTimeout);
		setValidInputs(defaultValids);
		setValidCheck(false);
		setError(null);
	};
	
	const onChange = e => {
		const { name, value } = e.target;
		switch(name.toLowerCase()) {
			case "firstname":
				setFirstName(value);
				break;
			case "lastname":
				setLastName(value);
				break;
			case "email":
				setEmail(value);
				break;
			case "uitimeout":
				setUiTimeout(value);
				break;
			default:
			// do nothing
		}
		
		if (validCheck && !validInputs[name]) checkInputValidity();
		if (isSuccessful) setIsSuccessful(false);
	};
	
	const onTypeChange = e => {
		const { value } = e.target;
		const thisType = roles.find(perm => perm.label === value);
		setUserType(thisType || roles[0]);
	};
	
	const onCompanyChange = e => {
		const { value } = e.target;
		const thisClient = clients.find(comp => comp.name === value);
		setCompany(thisClient || clients[0]);
		if (validCheck && !validInputs.company) checkInputValidity();
	};
	
	const checkInputValidity = () => {
		let newValids = {...validInputs};
		
		for (let key in newValids) {
			switch(key.toLowerCase()) {
				case "firstname":
					newValids[key] = Boolean(firstName.match(/^[a-z ,.'-]+$/i));
					break;
				case "lastname":
					newValids[key] = Boolean(lastName.match(/^[a-z ,.'-]+$/i));
					break;
				case "email":
					newValids[key] = Boolean(email.match(/^[\w.-]+@[a-zA-Z\d.-]+\.[a-zA-Z]{2,}$/));
					break;
				case "uitimeout":
					newValids[key] = uiTimeout >= 15 && uiTimeout <= 1440;
					break;
				case "company":
					newValids[key] = Boolean(company.clientId !== 0);
					break;
				default:
				// do nothing
			}
		}
		
		setValidInputs(newValids);
		setValidCheck(true);
		return Object.keys(newValids).every(key => newValids[key]);
	};
	
	const isValid = name => {
		if (!validCheck) return null;
		else if (name && !validInputs[name]) return !validInputs[name];
		return null;
	}
	
	const submitForm = async e => {
		e.preventDefault()
		const areAllInputsValid = checkInputValidity();
		
		if(areAllInputsValid) {
			const newUser = {
				firstName: capitalizeName(firstName.trim()),
				lastName: capitalizeName(lastName.trim()),
				name: `${capitalizeName(firstName.trim())} ${capitalizeName(lastName.trim())}`,
				email: email.toLowerCase(),
				client: company._id,
				lastLogin: Date.UTC(),
				role: userType.value,
				clientId: company.clientId,
				// company_name: company.label,
				uiTimeout: uiTimeout,
			};
			try {
				setIsPosting(true);
				let createUser;
				const token = await getAccessTokenSilently();
				if(user.role === 'local_admin') {
					createUser = await PutAuth(`/api/v6/admin/users-admin/${searchParams.get('user_id')}`, newUser, token);
				} else {
					createUser = await PutAuth(`/api/v6/admin/users/${searchParams.get('user_id')}`, newUser, token);
				}
				
				if (createUser) {
					if (createUser?.data_updated) {
						setDefaultState(createUser);
					} else {
						await getUser();
					}
					setIsSuccessful(true);
				}
				setIsPosting(false);
			} catch (err) {
				console.error(err);
				setError(err.message);
				setIsPosting(false);
			}
		}
	};
	
	const onDeleteClick = e => {
		e.preventDefault();
		setDeleteModalOpen(true);
	}
	
	const backToUsers = () => {
		navigate('/user/settings/admin/users/');
	}
	
	const onConfirmDeletionClick = async e => {
		e.preventDefault();
		try {
			setIsPosting(true);
			const token = await getAccessTokenSilently();
			const deleteUser = await DeleteAuth(`/api/v6/admin/users/${searchParams.get('user_id')}`, token);
			if (deleteUser) {
				setIsSuccessful(true);
			}
			setIsPosting(false);
			setDeleteModalOpen(false);
			backToUsers();
		} catch (err) {
			console.error(err);
			setError(err.message);
			setIsPosting(false);
		}
	};
	
	if (!searchParams.get('user_id')) return <Navigate to={"/user/settings/admin/users"}/>
	
	return <div>
		<VsiH4>Edit User</VsiH4>
		<VsiHr/>
		<Row>
			<Col xl={4} lg={5} md={12}>
				<Form onSubmit={submitForm}>
					<FormGroup>
						<Label for={"firstName"} className={`label-${theme}`}>
							First Name
						</Label>
						<Input id={"firstName"}
						       name={"firstName"}
						       invalid={isValid("firstName")}
						       placeholder={'VSI'}
						       value={firstName}
						       onChange={onChange}/>
						<FormFeedback>
							This is a required field and must be a valid name
						</FormFeedback>
					</FormGroup>
					<FormGroup>
						<Label for={"email"} className={`label-${theme}`}>
							Last Name
						</Label>
						<Input id={"lastName"}
						       name={"lastName"}
						       placeholder={'User'}
						       invalid={isValid("lastName")}
						       value={lastName}
						       onChange={onChange}/>
						<FormFeedback>
							This is a required field and must be a valid name
						</FormFeedback>
					</FormGroup>
					<FormGroup>
						<Label for={"email"} className={`label-${theme}`}>
							Email
						</Label>
						<Input id={"email"}
						       name={"email"}
						       type={"email"}
						       placeholder={'email@domain.com'}
						       value={email}
						       invalid={isValid("email")}
						       onChange={onChange}/>
						<FormFeedback>
							This is a required field and must be a valid email address
						</FormFeedback>
					</FormGroup>
					<FormGroup>
						<Label for={"userType"} className={`label-${theme}`}>
							User Type
						</Label>
						<Input id={"userType"} placeholder={'Normal User'} type={"select"} value={userType.label} onChange={onTypeChange}>
							{availableRoles.map((permission) => (
								<option key={`permission-${permission.value}`}>
									{permission.label}
								</option>
							))}
						</Input>
					</FormGroup>
					<FormGroup>
						<Label for={"company"} className={`label-${theme}`}>
							Company
						</Label>
						<Input id={"company"}
						       type={"select"}
						       value={company.label}
						       onChange={onCompanyChange}
						       invalid={isValid("company")}
						       placeholder={'Visual One Intelligence'}>
							{clients.map((client, index) => (
								<option key={`company-${index}`}>
									{client.name}
								</option>
							))}
						</Input>
						<FormFeedback>
							This is a required field
						</FormFeedback>
					</FormGroup>
					<FormGroup>
						<Label for={"uiTimeout"} className={`label-${theme}`}>
							UI Timeout (minutes)
						</Label>
						<Input id={"uiTimeout"}
						       name={"uiTimeout"}
						       type={"number"}
						       value={uiTimeout}
						       invalid={isValid("uiTimeout")}
						       onChange={onChange}/>
						<FormFeedback>
							UI Timeout may not be less than 15 minutes and no greater than 24 hours
						</FormFeedback>
					</FormGroup>
					{/*<FormGroup switch disabled>*/}
					{/*	<Input type="switch" checked={isTwoFactor} onChange={() => setTwoFactor(!isTwoFactor)}/>*/}
					{/*	<Label className={`label-${theme}`} check>Two Factor</Label>*/}
					{/*</FormGroup>*/}
					<Button type={"submit"} disabled={isPosting} color={'primary'}>
						{isPosting ? <Spinner size={'sm'}/> : 'Save Changes'}
					</Button>
					<Button className={'ms-2'} disabled={isPosting} color={'danger'} onClick={onDeleteClick}>
						{isPosting ? <Spinner size={'sm'}/> : 'Delete User'}
					</Button>
					{isSuccessful ? <VsiLgText className={'ms-2'}>User edited successfully!</VsiLgText> : null}
					{error ? <VsiLgText className={'ms-2'} style={{ color: '#dc3545'}}>{error}</VsiLgText> : null}
				</Form>
			</Col>
		</Row>
		<VsiModal isOpen={isDeleteModalOpen}
		          toggle={() => setDeleteModalOpen(!isDeleteModalOpen)}
		          header={<VsiSpanText>
			          Confirm user deletion: <strong>{`${firstName || ''} ${lastName || ''}`}</strong>
		          </VsiSpanText>}
		          footer={<>
			          <Button onClick={onConfirmDeletionClick} color={"danger"} className={"m-1"}>
				          Yes, Delete User
			          </Button>
			          <Button onClick={() => setDeleteModalOpen(false)} className={"m-1"}>
				          Cancel
			          </Button>
		          </>}>
			<>
				<VsiP>
					Are you sure you want to delete this user?
				</VsiP>
				<VsiP>
					This is a <strong>permanent action</strong> and cannot be undone.
				</VsiP>
			</>
		</VsiModal>
	</div>
}
